import Cookies from 'js-cookie';
import { COOKIE_NAME } from './constant';
import { teaReport } from '$tea';
import {
  CookieParam,
  CookieConsent,
  CookieConsentConfig,
  AsyncTask,
  CookieConsentWithVersion,
  Items,
  EntityKeys,
  groupEntityKeyItem,
} from '../interface/cookie-banner';
import { updateConsentRecords } from '../utils/consent';

function getDomain() {
  const hostname = window.location.hostname;
  const splitHostname = hostname.split('.');
  if (splitHostname.length > 3) {
    const len = splitHostname.length - 1;
    return `.${splitHostname.slice(-len).join('.')}`;
  } else {
    return `.${splitHostname.slice(-2).join('.')}`;
  }
}

function getPureConsentObject(consentObj: CookieConsent | CookieConsentWithVersion): CookieConsent {
  return Object.keys(consentObj)
    .filter((code) => code !== 'version')
    .reduce((obj: Record<string, boolean>, key: string) => {
      obj[key] = consentObj[key] as boolean;
      return obj;
    }, {});
}

function removeCookie() {
  Cookies.remove(COOKIE_NAME, {
    domain: getDomain(),
  });
  Cookies.remove(COOKIE_NAME);
  Cookies.remove(COOKIE_NAME, { domain: window.location.hostname });
}

function getConsentVersion(params?: Partial<CookieParam>): string | null {
  const { cookie } = params || {};
  const consent = cookie ? decodeURIComponent(cookie) : Cookies.get(COOKIE_NAME);
  if (!consent) {
    return null;
  }

  try {
    const consentObj = consent && JSON.parse(consent);
    if (consentObj && !consentObj.version) {
      removeCookie();
      return null;
    }
    return consentObj.version;
  } catch (e) {
    removeCookie();
    return null;
  }
}

/**
 * If both currentVersion and localVersion is written in 'v%d' format, compare the numbers after letter 'v'.
 * If not, fallback to string compare.
 */
function isConsentVersionNewer(currentVersion: string, localVersion: string | null): boolean {
  if (!localVersion) {
    return true;
  }

  const versionExp = /^v(\d+)$/;
  const matchCurrentVersion = versionExp.exec(currentVersion);
  const matchLocalVersion = versionExp.exec(localVersion);

  if (matchCurrentVersion && matchLocalVersion) {
    return Number(matchCurrentVersion[1]) > Number(matchLocalVersion[1]);
  }

  return currentVersion !== localVersion;
}

export function isEffectiveCountry(isEffective: boolean): string {
  return isEffective ? '1' : '0';
}

class CookieConfigManager {
  isRequestInflight = false;
  value: CookieConsentConfig | undefined;
  rawValue: any;
  pendingTasks: AsyncTask<CookieConsentConfig>[] = [];
  entityKeys: EntityKeys = {};
  collectionPointId: string = '';

  getSettings(): CookieConsentConfig['settings'] {
    if (!this.value?.settings?.length) {
      console.error('`CookieConfigManager.getSettings` returned undefined.');
      return [];
    }

    return this.value.settings;
  }

  getSettingItemKeys(items: Items, groupEntityKeys?: groupEntityKeyItem[]): string[] {
    const keys = Object.keys(items || {});
    if (keys.length) {
      return keys;
    }

    if (groupEntityKeys?.length) {
      return groupEntityKeys.map((item) => item.entityKey);
    }

    return [];
  }

  getCategoryConsents({ groupName, value }: { groupName: string; value: boolean }): CookieConsent {
    const settings = this.getSettings();
    const category = settings.find((setting) => setting.groupName === groupName);
    if (!category) {
      console.error('`CookieConfigManager.getCategoryConsents` returned category undefined.');
      return {};
    }

    const consent = this.getSettingItemKeys(category.items, category.groupEntityKeys).reduce(
      (obj: Record<string, boolean>, key: string) => {
        obj[key] = value;
        return obj;
      },
      {},
    );

    return consent;
  }

  setFullConsents(): CookieConsent {
    const consent = this.getFullConsents();
    return this.setCookieConsents(consent);
  }

  getFullConsents(): CookieConsent {
    const settings = this.getSettings();
    let consent: CookieConsent = {};
    settings.forEach((setting) => {
      const categoryConsent = this.getCategoryConsents({ groupName: setting.groupName, value: true });
      consent = {
        ...consent,
        ...categoryConsent,
      };
    });

    return consent;
  }

  setEmptyConsents(): CookieConsent {
    const consent = this.getEmptyConsents();
    return this.setCookieConsents(consent);
  }

  getEmptyConsents(): CookieConsent {
    const settings = this.getSettings();
    let consent: CookieConsent = {};
    settings.forEach((setting) => {
      const categoryConsent = this.getCategoryConsents({ groupName: setting.groupName, value: false });
      consent = {
        ...consent,
        ...categoryConsent,
      };
    });

    return consent;
  }

  setCookieConsents(value: CookieConsent): CookieConsent {
    const settings = this.getSettings();
    if (!settings.length) {
      return {};
    }

    settings.forEach((setting) => {
      const keys = Object.keys(setting.items);
      for (const key of keys) {
        if (!value[key]) {
          const { cookies = [] } = setting.items[key] || {};
          cookies.forEach((cookie) =>
            Cookies.remove(cookie, {
              domain: getDomain(),
            }),
          );
        }
      }
    });

    const version = this.value?.version || '';
    const consentCookieValue = JSON.stringify({ ...value, version });
    Cookies.set(COOKIE_NAME, consentCookieValue, {
      expires: 30 * 13,
      domain: getDomain(),
      secure: true,
      sameSite: 'None',
    });

    setTimeout(() => {
      if (Cookies.get(COOKIE_NAME) !== consentCookieValue) {
        teaReport('cookie_banner_save_cookie_failed');
      }
    }, 100);

    this.updateConsentRecords(value);

    return value;
  }

  updateConsentRecords(value: CookieConsent): void {
    const consent: { key: string; value: boolean }[] = [];
    Object.keys(value).forEach((key) => {
      const consentKey = this.entityKeys?.[key] || [];
      if (!consentKey?.length) {
        return;
      }

      const val = value[key];
      const consentRecords = consentKey.map((item) => ({ key: item, value: val }));
      consent.push(...consentRecords);
    });
    updateConsentRecords(consent, this.collectionPointId);
  }

  /*
   * { ga: boolean, af: boolean }
   **/
  getCookieConsents(params: CookieParam): CookieConsent {
    const settings = this.getSettings();
    if (!settings.length) {
      return {};
    }

    const { isEffective, cookie, isFTC } = params;
    const groupEntityKeys: groupEntityKeyItem[] = [];
    const items = settings.reduce((o, setting) => {
      const keys = setting.groupEntityKeys || [];
      let groupItems = {};

      if (keys.length) {
        groupEntityKeys.push(...keys);
        groupItems = keys.reduce(
          (obj, item) => ({ ...obj, [item.entityKey]: { default: item.default, cookies: item.cookies } }),
          {},
        );
      }

      return { ...o, ...(setting?.items || {}), ...groupItems };
    }, {});
    const keys = this.getSettingItemKeys(items);
    const entityKeys = groupEntityKeys.map((item) => item.entityKey);
    keys.push(...entityKeys);

    try {
      const consent = cookie ? decodeURIComponent(cookie) : Cookies.get(COOKIE_NAME);
      const notEuroCountryConsents = keys.reduce((obj: Record<string, boolean>, key: string) => {
        obj[key] = true;
        return obj;
      }, {});
      const consentObj = consent && JSON.parse(consent);
      // Cannot use third-party cookies when is US FTC User
      if (isFTC) {
        return this.getEmptyConsents();
      }

      // have consent info stored in browser
      if (consentObj) {
        return getPureConsentObject(
          Object.assign({}, isEffective ? this.getDefaultConsents(items) : notEuroCountryConsents, consentObj),
        );
      }

      // do not have consent info stored in browser
      if (!isEffective) {
        return notEuroCountryConsents;
      }
    } catch (e) {}

    return this.getDefaultConsents(items);
  }

  getDefaultConsents(items: Items): CookieConsent {
    const keys = Object.keys(items || {});
    if (keys.length) {
      const consent: CookieConsent = {};
      keys.forEach((key) => {
        consent[key] = !!items[key].default;
      });

      return consent;
    }

    return {};
  }

  isShowCookieBanner(params: CookieParam): boolean {
    const { isEffective } = params;
    const localVersion = getConsentVersion(params);
    if (!isEffective) {
      // in non-euro countries, we are allowed to use any third-party cookies without informing users
      return false;
    }

    if (!this.value) {
      return false;
    }

    const version = this.value?.version || '';
    const shouldShowBanner = isConsentVersionNewer(version, localVersion);
    if (!shouldShowBanner) {
      return false;
    }

    // 浏览器无法写入 cookie，则不展示 banner
    const shouldNotShowBanner = this.shouldNotShowBanner();
    if (shouldNotShowBanner) {
      return false;
    }

    return true;
  }

  setCookieConfig(cookieConfig: CookieConsentConfig & { entityKeys: EntityKeys; collectionPointId: string }): void {
    const { entityKeys, collectionPointId, ...restConfig } = cookieConfig;
    this.value = restConfig;
    this.entityKeys = entityKeys || {};
    this.collectionPointId = collectionPointId || '';
  }

  shouldNotShowBanner(): boolean {
    const value = Cookies.get();
    if (value && typeof value === 'object' && Object.keys(value).length) {
      return false;
    }

    Cookies.set(COOKIE_NAME, '1', {
      expires: 1,
      domain: getDomain(),
      secure: true,
      sameSite: 'None',
    });

    const cookieValue = Cookies.get(COOKIE_NAME);
    const setCookieAvailable = cookieValue !== undefined;
    if (!setCookieAvailable) {
      return true;
    }

    removeCookie();
    return false;
  }
}

const instance = new CookieConfigManager();

export default instance;

export type ICookieConfigManager = typeof instance;
